import React, { useRef, useEffect } from 'react';
import { useGLTF } from '@react-three/drei';
import * as THREE from 'three';

// ✅ BONE_MAPPING now maps to the correct bone names in the GLB model
const BONE_MAPPING = {
  'Hips': 'Humano_Hip',
  'Spine': 'Humano_Waist',
  'Spine1': 'Humano_Spine01',
  'Spine2': 'Humano_Spine02',
  'Neck': 'Humano_NeckTwist01',
  'Head': 'Humano_Head',
  'LeftShoulder': 'Humano_L_Clavicle',
  'LeftUpperArm': 'Humano_L_Upperarm', 
  'LeftLowerArm': 'Humano_L_Forearm',
  'LeftHand': 'Humano_L_Hand',
  'RightShoulder': 'Humano_R_Clavicle',
  'RightUpperArm': 'Humano_R_Upperarm',
  'RightLowerArm': 'Humano_R_Forearm',
  'RightHand': 'Humano_R_Hand',
  'LeftUpperLeg': 'Humano_L_Thigh',
  'LeftLowerLeg': 'Humano_L_Calf',
  'LeftFoot': 'Humano_L_Foot',
  'LeftToeBase': 'Humano_L_ToeBase',
  'RightUpperLeg': 'Humano_R_Thigh',
  'RightLowerLeg': 'Humano_R_Calf',
  'RightFoot': 'Humano_R_Foot',
  'RightToeBase': 'Humano_R_ToeBase',
};

const HumanModel = ({ modelPath, poseWorldLandmarks, realHeight }) => {
  const { scene } = useGLTF(modelPath);
  const skeletonRef = useRef(null);
  const initialModelHeightRef = useRef(null);

  // Helper function to calculate the Euclidean distance between two 3D points
  const getDistance = (p1, p2) => {
    return Math.sqrt(
      Math.pow(p2.x - p1.x, 2) +
      Math.pow(p2.y - p1.y, 2) +
      Math.pow(p2.z - p1.z, 2)
    );
  };

  // Combined useEffect to handle both model initialization and landmark processing
  useEffect(() => {
    // Check if we have both the model scene and valid landmarks
    if (!scene || !poseWorldLandmarks || poseWorldLandmarks.length < 32 || !realHeight) {
      console.log('Waiting for model scene, valid pose world landmarks data, or realHeight...');
      return;
    }

    // --- 1. Model Initialization (Run only once) ---
    // Check if the skeleton and initial height have been stored
    if (!skeletonRef.current || !initialModelHeightRef.current) {
        scene.traverse((object) => {
            if (object.isSkinnedMesh) {
              if (object.skeleton && object.skeleton.bones.length > 0) {
                skeletonRef.current = object.skeleton;
                
                // Store the initial height of the model
                const skeleton = object.skeleton;
                const headBone = skeleton.bones.find(b => b.name === BONE_MAPPING.Head);
                const leftFootBone = skeleton.bones.find(b => b.name === BONE_MAPPING.LeftFoot);
                const rightFootBone = skeleton.bones.find(b => b.name === BONE_MAPPING.RightFoot);
    
                if (headBone && leftFootBone && rightFootBone) {
                    // Find the lowest point between the two feet
                    const lowestFoot = Math.min(leftFootBone.position.y, rightFootBone.position.y);
                    initialModelHeightRef.current = headBone.position.y - lowestFoot;
                }
    
                console.log("✅ SkinnedMesh and Skeleton found. Initial model height stored.");
                return;
              }
            }
          });
    }

    // Only proceed if initialization is complete
    const skeleton = skeletonRef.current;
    const initialModelHeight = initialModelHeightRef.current;
    if (!skeleton || !initialModelHeight) {
        console.log('Model initialization not yet complete. Retrying on next frame.');
        return;
    }

    // --- 2. Calculate the user's total height from landmarks ---
    console.log("📐 Calculating user's total height from landmarks...");
    const landmarks = poseWorldLandmarks;

    // Find the highest landmark (head) and lowest landmarks (feet)
    const headLandmark = landmarks[0];
    const leftFootLandmark = landmarks[29];
    const rightFootLandmark = landmarks[30];

    if (!headLandmark || !leftFootLandmark || !rightFootLandmark) {
      console.error("Could not find head or foot landmarks. Cannot calculate user's height.");
      return;
    }
    
    // Calculate the lowest point between the two feet
    const lowestFootY = Math.min(leftFootLandmark.y, rightFootLandmark.y);
    const userHeightInRelativeMeters = lowestFootY - headLandmark.y;
    console.log(`Calculated height in relative meters: ${userHeightInRelativeMeters.toFixed(2)} m`);

    // --- 3. Calculate the global scaling factor using the user's real height ---
    const scaleFactor = realHeight / initialModelHeight;
    console.log("Calculated Global Scale Factor using real height:", scaleFactor);
    
    // --- 4. Apply the scaling to the entire model ---
    console.log("Applying global scale to model...");
    scene.scale.set(scaleFactor, scaleFactor, scaleFactor);

    // --- 5. Log individual body measurements in meters for reference ---
    // Note: We use the real height to scale the relative measurements for an accurate log
    const conversionFactor = realHeight / userHeightInRelativeMeters;
    console.log("📏 User Body Measurements (in meters):");
    console.log(`Height: ${realHeight.toFixed(2)} m`);

    // Torso length (shoulders to hips)
    const leftShoulder = landmarks[11];
    const rightShoulder = landmarks[12];
    const leftHip = landmarks[23];
    const rightHip = landmarks[24];
    const avgShoulderY = (leftShoulder.y + rightShoulder.y) / 2;
    const avgHipY = (leftHip.y + rightHip.y) / 2;
    const torsoLength = (avgHipY - avgShoulderY) * conversionFactor;
    console.log(`Torso Length: ${torsoLength.toFixed(2)} m`);

    // Shoulders width
    const shouldersWidth = getDistance(leftShoulder, rightShoulder) * conversionFactor;
    console.log(`Shoulders Width: ${shouldersWidth.toFixed(2)} m`);

    // Hips width
    const hipsWidth = getDistance(leftHip, rightHip) * conversionFactor;
    console.log(`Hips Width: ${hipsWidth.toFixed(2)} m`);

    // Left Arm length (shoulder to wrist)
    const leftUpperArm = getDistance(landmarks[11], landmarks[13]) * conversionFactor;
    const leftLowerArm = getDistance(landmarks[13], landmarks[15]) * conversionFactor;
    const leftArmLength = leftUpperArm + leftLowerArm;
    console.log(`Left Arm Length: ${leftArmLength.toFixed(2)} m`);
    console.log(`  - Left Upper Arm: ${leftUpperArm.toFixed(2)} m`);
    console.log(`  - Left Lower Arm: ${leftLowerArm.toFixed(2)} m`);

    // Right Arm length (shoulder to wrist)
    const rightUpperArm = getDistance(landmarks[12], landmarks[14]) * conversionFactor;
    const rightLowerArm = getDistance(landmarks[14], landmarks[16]) * conversionFactor;
    const rightArmLength = rightUpperArm + rightLowerArm;
    console.log(`Right Arm Length: ${rightArmLength.toFixed(2)} m`);
    console.log(`  - Right Upper Arm: ${rightUpperArm.toFixed(2)} m`);
    console.log(`  - Right Lower Arm: ${rightLowerArm.toFixed(2)} m`);

    // Left Leg length (hip to ankle)
    const leftThigh = getDistance(landmarks[23], landmarks[25]) * conversionFactor;
    const leftCalf = getDistance(landmarks[25], landmarks[27]) * conversionFactor;
    const leftLegLength = leftThigh + leftCalf;
    console.log(`Left Leg Length: ${leftLegLength.toFixed(2)} m`);
    console.log(`  - Left Thigh: ${leftThigh.toFixed(2)} m`);
    console.log(`  - Left Calf: ${leftCalf.toFixed(2)} m`);

    // Right Leg length (hip to ankle)
    const rightThigh = getDistance(landmarks[24], landmarks[26]) * conversionFactor;
    const rightCalf = getDistance(landmarks[26], landmarks[28]) * conversionFactor;
    const rightLegLength = rightThigh + rightCalf;
    console.log(`Right Leg Length: ${rightLegLength.toFixed(2)} m`);
    console.log(`  - Right Thigh: ${rightThigh.toFixed(2)} m`);
    console.log(`  - Right Calf: ${rightCalf.toFixed(2)} m`);
    
  }, [modelPath, poseWorldLandmarks, scene, realHeight]);

  // New useEffect to handle positioning the model
  useEffect(() => {
    if (scene) {
      // First, rotate the model 180 degrees around the X-axis to flip it upright
      scene.rotation.x = Math.PI;

      // Get the bounding box of the entire model
      const box = new THREE.Box3().setFromObject(scene);
      
      // Calculate the distance from the bottom of the model to the origin
      const heightBelowOrigin = box.min.y;
      
      // Translate the model up by that distance to place its feet on the ground (y=0)
      scene.position.y = -0;
      
      console.log(`✅ Model repositioned. Height below origin: ${heightBelowOrigin.toFixed(2)}`);
    }
  }, [scene]);

  return (
    <primitive object={scene} castShadow receiveShadow />
  );
};

export default HumanModel;
